import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfigProvider, useConfig } from './context/ConfigContext';
import Layout from './components/Layout';

// Critical pages imported directly to ensure instant loading without chunk failures
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import Agenda from './pages/Agenda';
import Speakers from './pages/Speakers';
import UserQuestions from './pages/UserQuestions';
const Streaming = lazy(() => import('./pages/Streaming'));
const Profile = lazy(() => import('./pages/Profile'));
const Scanner = lazy(() => import('./pages/Scanner'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const StaffQuestions = lazy(() => import('./pages/StaffQuestions'));
const StaffGallery = lazy(() => import('./pages/StaffGallery'));
const EventGallery = lazy(() => import('./pages/EventGallery'));
const WelcomeLetter = lazy(() => import('./pages/WelcomeLetter'));
const ThankYouLetter = lazy(() => import('./pages/ThankYouLetter'));
const Certificate = lazy(() => import('./pages/Certificate'));
const InteractivePoll = lazy(() => import('./pages/InteractivePoll'));
const StaffPollManagement = lazy(() => import('./pages/StaffPollManagement'));
const StaffPollResults = lazy(() => import('./pages/StaffPollResults'));
const Survey = lazy(() => import('./pages/Survey'));
const BingoLauncher = lazy(() => import('./pages/BingoLauncher'));
const BingoStaff = lazy(() => import('./pages/BingoStaff'));
const StaffConfig = lazy(() => import('./pages/StaffConfig'));
const DynamicPage = lazy(() => import('./pages/DynamicPage'));

const PageLoader = () => (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <span className="font-black text-[10px] uppercase tracking-[0.3em] text-slate-300">Cargando...</span>
    </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, pageKey, allowRoles }) => {
    const { user, loading: authLoading } = useAuth();
    const { config, loading: configLoading } = useConfig();

    if (authLoading || configLoading) return <PageLoader />;

    if (!user) return <Navigate to="/login" />;

    // Check if the page is disabled in config
    if (pageKey && config?.pages?.[pageKey] === false) {
        return <Navigate to="/" />;
    }

    // Role check
    if (allowRoles && !allowRoles.includes(user.role)) {
        return <Navigate to="/" />;
    }

    return <Layout>{children}</Layout>;
};

const AppRoutes = () => {
    const { config } = useConfig();

    return (
        <Router>
            <Suspense fallback={<PageLoader />}>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/survey" element={<Layout><Survey /></Layout>} />

                    {/* Protected Routes */}
                    <Route path="/" element={<ProtectedRoute pageKey="home"><Home /></ProtectedRoute>} />
                    <Route path="/agenda" element={<ProtectedRoute pageKey="agenda"><Agenda /></ProtectedRoute>} />
                    <Route path="/speakers" element={<ProtectedRoute pageKey="speakers"><Speakers /></ProtectedRoute>} />
                    <Route path="/streaming" element={<ProtectedRoute pageKey="streaming"><Streaming /></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute pageKey="profile"><Profile /></ProtectedRoute>} />
                    <Route path="/scanner" element={<ProtectedRoute allowRoles={['staff', 'admin']}><Scanner /></ProtectedRoute>} />
                    <Route path="/staff-questions" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffQuestions /></ProtectedRoute>} />
                    <Route path="/staff-gallery" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffGallery /></ProtectedRoute>} />
                    <Route path="/event-gallery" element={<ProtectedRoute><EventGallery /></ProtectedRoute>} />
                    <Route path="/welcome" element={<ProtectedRoute pageKey="welcome"><WelcomeLetter /></ProtectedRoute>} />
                    <Route path="/thanks" element={<ProtectedRoute pageKey="thanks"><ThankYouLetter /></ProtectedRoute>} />
                    <Route path="/certificate" element={<ProtectedRoute pageKey="certificate"><Certificate /></ProtectedRoute>} />
                    <Route path="/questions-expert" element={<ProtectedRoute pageKey="questions_private"><UserQuestions /></ProtectedRoute>} />
                    <Route path="/interactiva" element={<ProtectedRoute><InteractivePoll /></ProtectedRoute>} />
                    <Route path="/interactiva-gestion" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffPollManagement /></ProtectedRoute>} />
                    <Route path="/interactiva-stats/:id" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffPollResults /></ProtectedRoute>} />
                    <Route path="/bingo" element={<ProtectedRoute><BingoLauncher /></ProtectedRoute>} />
                    <Route path="/bingo-staff" element={<ProtectedRoute allowRoles={['staff', 'admin']}><BingoStaff /></ProtectedRoute>} />
                    <Route path="/bingo-staff-config" element={<ProtectedRoute allowRoles={['staff', 'admin']}><StaffConfig /></ProtectedRoute>} />

                    {/* Dynamic Gallery Routes */}
                    {config?.customGalleryPages?.map(page => (
                        <Route
                            key={page.slug}
                            path={`/gallery/${page.slug}`}
                            element={<ProtectedRoute><GalleryPage /></ProtectedRoute>}
                        />
                    ))}

                    {/* Custom CMS Pages */}
                    <Route path="/p/:slug" element={<ProtectedRoute><DynamicPage /></ProtectedRoute>} />

                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </Suspense>
        </Router>
    );
};

function App() {
    return (
        <ConfigProvider>
            <AuthProvider>
                <AppRoutes />
            </AuthProvider>
        </ConfigProvider>
    );
}

export default App;
